/**
 * CommitmentProgress - Fuel commitment progress bar with risk indicator
 *
 * Shared between FuelTab and FinancialTab. Shows progress bar,
 * delivered vs committed gallons, days remaining, and risk warnings.
 */

import { Fuel, Calendar, AlertTriangle } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import {
  calculateCommitmentProgress,
  isCommitmentAtRisk,
} from '@/types/fuel-transactions'
import { CompactInfoBlock } from './CompactInfoBlock'
import { TooltipValue, CompactRow } from './TooltipValue'

interface CommitmentProgressProps {
  committedGallons: number
  deliveredGallons: number
  daysRemaining: number
  pricePerGallon: number | null
  priceType: string | null
}

export function CommitmentProgress({
  committedGallons,
  deliveredGallons,
  daysRemaining,
  pricePerGallon,
  priceType,
}: CommitmentProgressProps) {
  const percentage = calculateCommitmentProgress(deliveredGallons, committedGallons)
  const remainingGallons = Math.max(0, committedGallons - deliveredGallons)
  const atRisk = isCommitmentAtRisk(percentage, daysRemaining)

  // Calculate vs-retail comparison if we have contract price
  const savingsDisplay = pricePerGallon && deliveredGallons > 0
    ? `@ $${pricePerGallon.toFixed(2)}/gal (${priceType || 'contract'})`
    : null

  return (
    <CompactInfoBlock title="Fuel Commitment">
      <div className="space-y-2">
        {/* Progress bar */}
        <Progress
          value={percentage}
          className={`h-2 ${atRisk ? '[&>div]:bg-amber-500' : percentage >= 100 ? '[&>div]:bg-green-500' : ''}`}
        />

        {/* Stats row */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5">
            <Fuel className="w-3 h-3 text-muted-foreground" />
            <span className="text-muted-foreground">
              {deliveredGallons.toLocaleString()} / {committedGallons.toLocaleString()} gal
            </span>
          </div>
          <span className={`font-semibold ${atRisk ? 'text-amber-600' : percentage >= 100 ? 'text-green-600' : 'text-primary'}`}>
            {percentage}%
          </span>
        </div>

        {/* Remaining and days row */}
        <CompactRow>
          <TooltipValue
            label="Remaining to commit"
            value={`${remainingGallons.toLocaleString()} gal left`}
            variant={atRisk ? 'warning' : 'muted'}
          />
          <TooltipValue
            label="Days remaining this month"
            value={`${daysRemaining} days`}
            icon={<Calendar className="w-3 h-3" />}
            variant={daysRemaining <= 7 ? 'warning' : 'muted'}
          />
        </CompactRow>

        {/* Contract price info */}
        {savingsDisplay && (
          <div className="text-[10px] text-muted-foreground">
            {savingsDisplay}
          </div>
        )}

        {/* Risk warning */}
        {atRisk && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
            <AlertTriangle className="w-3 h-3" />
            <span>At risk - below target pace</span>
          </div>
        )}
      </div>
    </CompactInfoBlock>
  )
}
